import {
  PERMISSION_CATEGORIES,
  permissionParentMap,
  type PermissionCategories,
  type PermissionCategoryKey,
  type Role,
} from "./types";

type PermissionMatrix = Record<string, Record<string, boolean>>;

export const validateRolesHavePermissions = (
  data: PermissionMatrix,
  roles: Role[]
): { valid: boolean; rolesWithoutPermissions: string[] } => {
  const rolePermissionCounts = new Map<string, number>();

  // Initialize all roles with 0 permisssions
  roles.forEach((role) => rolePermissionCounts.set(role.name, 0));

  // count permissions per role
  for (const [permission, roleMap] of Object.entries(data)) {
    for (const [roleName, hasPermission] of Object.entries(roleMap)) {
      if (hasPermission) {
        rolePermissionCounts.set(
          roleName,
          (rolePermissionCounts.get(roleName) || 0) + 1
        );
      }
    }
  }

  const rolesWithoutPermissions: string[] = [];
  for (const [roleName, count] of rolePermissionCounts.entries()) {
    if (count === 0) {
      rolesWithoutPermissions.push(roleName);
    }
  }

  return {
    valid: rolesWithoutPermissions.length === 0,
    rolesWithoutPermissions
  };
};

export const categorizePermissions = (
  permissions: string[],
  categories: PermissionCategories = PERMISSION_CATEGORIES
): Record<string, string[]> => {
  const categorized: Record<string, string[]> = {
    systemAccess: [],
    bookingManagement: [],
    insights: [],
    assetTracking: [],
    lanePlanning: [],
    vehicleCondition: [],
    buses: [],
    staffManagement: [],
    uncategorized: []
  };

  permissions.forEach(permission => {
    // Prevent the same permission from being added to multiple categories
    let assigned = false;

    Object.entries(categories).forEach(([categoryKey, category]) => {
      for (const subCategory of category.subCategories) {
        if (!assigned && (subCategory.permissions as readonly string[]).includes(permission)) {
          categorized[categoryKey as PermissionCategoryKey].push(permission);
          assigned = true;
        }
      }
    });

    if (!assigned) {
      categorized.uncategorized.push(permission);
    }
  });

  return categorized;
};

export const mapPermissionDataToRoleInput = (data: Record<string, Record<string, boolean>>) => {
  const roleMap = new Map<string, string[]>();

  for (const [permission, roles] of Object.entries(data)) {
    for (const [role, hasAccess] of Object.entries(roles)) {
      if (hasAccess) {
        if (!roleMap.has(role)) {
          roleMap.set(role, []);
        }
        roleMap.get(role)!.push(permission);
      }
    }
  }

  return Array.from(roleMap.entries()).map(
    ([name, permissions]) => ({ name, permissions })
  );
};

export const normalizePermissionMatrix = (data: PermissionMatrix): PermissionMatrix => {
  const normalizedData: PermissionMatrix = Object.fromEntries(
    Object.entries(data).map(([permission, roles]) => [permission, { ...roles }])
  );

  for (const [permission, parentPermission] of permissionParentMap.entries()) {
    const roleMap = normalizedData[permission];

    if (!roleMap) {
      continue;
    }

    for (const roleName of Object.keys(roleMap)) {
      if (normalizedData[parentPermission]?.[roleName] !== true) {
        roleMap[roleName] = false;
      }
    }
  }

  return normalizedData;
};

export const buildInitialPermissionMatrix = (
  roles: Array<{ name: string; permissions: string[] }>,
  allPermissions: string[]
): Record<string, Record<string, boolean>> => {
  const matrix: Record<string, Record<string, boolean>> = {};

  allPermissions.forEach(permission => {
    matrix[permission] = {};
    roles.forEach(role => {
      matrix[permission][role.name] = role.permissions.includes(permission);
    });
  });

  return matrix;
};
